import React, { FC, ReactNode, useState } from "react";
import Button, { ButtonVariants } from "./Button";
import Animation from "./Animation";
import { styled } from "@mui/system";
import { COLORS, FONTS, FONT_WEIGHTS, SIZES, SPACINGS } from "../../common/consts";
import { makeShouldForwardProps } from "../../common/utils/ui";

interface Props {
  options: {
    label: ReactNode;
    value?: string;
    disabled?: boolean;
    children?: ReactNode;
    onClick?: () => void;
  }[];
  className?: string;
  /** The tab to open on first render, when not the first one. */
  initialValue?: string;
}

interface NavLinkProps {
  active: boolean;
}

const shouldForwardProp = makeShouldForwardProps<NavLinkProps>(["active"]);
/* Folder-tab pattern matching Outreach: top-rounded only so the active tab
   reads as attached to the panel below, tabs flush against each other, and a
   purple-tinted active fill against neutral inactive labels. */
const NavLink = styled(Button, {
  shouldForwardProp,
})<NavLinkProps>`
  /* !important is needed here: these render as Button variant=light, and the
     global .btn-light dark rule otherwise paints a grey box on every tab. */
  color: ${({ active }) =>
    active
      ? `var(--color-tabActiveText, ${COLORS.tabActiveText})`
      : `var(--color-tabInactiveText, ${COLORS.tabInactiveText})`} !important;
  background-color: ${({ active }) =>
    active ? `var(--color-tabActiveBg, ${COLORS.tabActiveBg})` : "transparent"} !important;
  border-radius: 0.375rem 0.375rem 0 0;
  padding: 0 ${SPACINGS.lg};
  font: ${FONTS.sm};
  font-size: 0.9375rem;
  border: none;
  font-weight: ${({ active }) => (active ? FONT_WEIGHTS.semibold : SIZES.navigationFontWeight)};
  cursor: pointer;
  height: 2.5rem;
  transition: background-color 0.2s ease, color 0.2s ease;

  &:hover {
    color: ${({ active }) =>
      active
        ? `var(--color-tabActiveText, ${COLORS.tabActiveText})`
        : `var(--color-tabInactiveText, ${COLORS.tabInactiveText})`} !important;
    background-color: ${({ active }) =>
      active
        ? `var(--color-tabActiveBg, ${COLORS.tabActiveBg})`
        : `var(--color-tabHoverBg, ${COLORS.tabHoverBg})`} !important;
  }

  &:active,
  &.active {
    color: var(--color-tabActiveText, ${COLORS.tabActiveText});
    background-color: var(--color-tabActiveBg, ${COLORS.tabActiveBg}) !important;
  }

  &:focus {
    color: ${({ active }) =>
      active
        ? `var(--color-tabActiveText, ${COLORS.tabActiveText})`
        : `var(--color-tabInactiveText, ${COLORS.tabInactiveText})`} !important;
    background-color: ${({ active }) =>
      active
        ? `var(--color-tabActiveBg, ${COLORS.tabActiveBg})`
        : "transparent"} !important;
    outline: none !important;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const StyledBox = styled("div")`
  margin-bottom: 0;

  /* Outreach butts its tabs together; the padding supplies the separation. */
  & > ul.nav {
    gap: 0;
  }
`;

interface NavItemProps {
  isFirst: boolean;
}

const navItemShouldForwardProp = makeShouldForwardProps<NavItemProps>(["isFirst"]);
const StyledNavItem = styled("li", {
  shouldForwardProp: navItemShouldForwardProp,
})<NavItemProps>`
  ${({ isFirst }) => isFirst && `margin-left: ${SPACINGS.sm};`}
`;

const Tabs: FC<Props> = ({ options, className = "", initialValue }) => {
  const [activeTab, setActiveTab] = useState(
    initialValue && options.some((option) => option.value === initialValue) ? initialValue : options[0].value || ""
  );

  return (
    <>
      <StyledBox className="boring-tabs">
        <ul className={`nav ${className}`}>
          {options.map((option, index) => (
            <StyledNavItem key={option.value} className="nav-item" isFirst={index === 0}>
              <NavLink
                active={option.value === activeTab}
                className={option.value === activeTab ? "boring-tab boring-tab-active" : "boring-tab"}
                type="button"
                variant={ButtonVariants.light}
                disabled={option.disabled}
                onClick={() => {
                  setActiveTab(option.value || "");
                  option.onClick?.();
                }}
              >
                {option.label}
              </NavLink>
            </StyledNavItem>
          ))}
        </ul>
      </StyledBox>
      <Animation key={activeTab}>
        {options.find((option) => option.value === activeTab)?.children || null}
      </Animation>
    </>
  );
};

export default Tabs;
